import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { isRestrictedArchitecture } from '../../../capabilities/image-vm/utils';

/** 读取表单 GPU 字段（resource.gpu） */
export function readGpu(values: Record<string, unknown>): Record<string, unknown> | undefined {
  const gpu = get(values, 'resource.gpu');
  return gpu && typeof gpu === 'object' ? (gpu as Record<string, unknown>) : undefined;
}

/** 从 getFieldsValue 结果读取标量（兼容嵌套 path 与 legacy 扁平 key） */
export function readFormScalar(values: Record<string, unknown>, path: string): unknown {
  const nested = get(values, path);
  if (nested !== undefined && nested !== null && nested !== '') return nested;
  return values[path];
}

/** 资源配置模块：仅读取用户选择的 system.architecture */
export function readFormArchitecture(values: Record<string, unknown>): string | undefined {
  const text = String(readFormScalar(values, 'system.architecture') ?? '').trim();
  return text || undefined;
}

/** 从容器镜像表单字段读取镜像支持的 CPU 架构（非用户选择的 system.architecture） */
export function readContainerImageArchitecture(values: Record<string, unknown>): string | undefined {
  const image = get(values, 'basic.image') as { architecture?: string } | undefined;
  const arch = get(values, 'basic.image.architecture') ?? get(values, 'basic.image.cpuArchitectures[0]') ?? image?.architecture;
  if (!isRestrictedArchitecture(arch)) return undefined;
  return String(arch).trim();
}

/** 从系统盘镜像读取 CPU 架构（非 system.architecture） */
export function readSystemDiskImageArchitecture(values: Record<string, unknown>): string | undefined {
  const disk = get(values, 'systemDisk');
  if (!disk || typeof disk !== 'object') return undefined;
  const arch = (disk as { image?: { architecture?: string } }).image?.architecture;
  if (!isRestrictedArchitecture(arch)) return undefined;
  return String(arch).trim();
}

/** 读取单字段当前值（优先 getFieldValue） */
export function readCurrentFieldValue(form: FormInstance, path: string): unknown {
  if (typeof form.getFieldValue === 'function') {
    return form.getFieldValue(path);
  }
  return get(form.getFieldsValue() as Record<string, unknown>, path);
}

/** 仅在值变化时写入，避免 linkage 重复触发 */
export function setFieldValueIfChanged(form: FormInstance, path: string, next: unknown): void {
  const current = readCurrentFieldValue(form, path);
  if (current === next) return;
  if (current == null && (next == null || next === undefined)) return;
  form.setFieldValue(path, next);
}
